import { useState } from "react";
import { Phone, Mail, MapPin, Send, CheckCircle } from "lucide-react";
import axios from "axios";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import { trackEvent } from "@/utils/tracking";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_OPTIONS = [
  "Professional TV Mounting",
  "Smart Home Installation",
  "Remote & On-Site IT Support",
  "Wi-Fi & Network Setup",
  "Home Theater & Audio/Video Setup",
  "Small Business IT Solutions",
  "Yacht & Marine Tech Installation",
  "Free Consultation",
];

function ContactInfo() {
  return (
    <div>
      <p className="text-xs font-bold tracking-[0.2em] uppercase text-blue-400 mb-4">
        Get In Touch
      </p>
      <h2
        className="text-3xl md:text-4xl tracking-tight font-medium mb-6"
        style={{ fontFamily: 'Outfit, sans-serif' }}
      >
        Let's Work Together
      </h2>
      <p className="text-slate-400 text-lg leading-relaxed mb-10">
        Ready to upgrade your tech? Fill out the form and we'll get back to you within 24 hours.
        Or call us directly for immediate assistance.
      </p>

      <div className="space-y-6">
        <a href="tel:2398344377" data-testid="contact-phone"
          onClick={() => trackEvent("call_click", { location: "contact-section" })}
          className="flex items-center gap-4 group">
          <div className="w-12 h-12 rounded-xl bg-blue-600/15 flex items-center justify-center group-hover:bg-blue-600/25 transition-colors">
            <Phone size={20} className="text-blue-400" />
          </div>
          <div>
            <p className="text-white font-medium">239-834-4377</p>
            <p className="text-slate-500 text-sm">Mon - Sat, 8am - 8pm</p>
          </div>
        </a>

        <a href="mailto:jasont@techhelpswfl.com" data-testid="contact-email" className="flex items-center gap-4 group">
          <div className="w-12 h-12 rounded-xl bg-blue-600/15 flex items-center justify-center group-hover:bg-blue-600/25 transition-colors">
            <Mail size={20} className="text-blue-400" />
          </div>
          <div>
            <p className="text-white font-medium">jasont@techhelpswfl.com</p>
            <p className="text-slate-500 text-sm">We respond within 24 hours</p>
          </div>
        </a>

        <div className="flex items-center gap-4" data-testid="contact-location">
          <div className="w-12 h-12 rounded-xl bg-blue-600/15 flex items-center justify-center">
            <MapPin size={20} className="text-blue-400" />
          </div>
          <div>
            <p className="text-white font-medium">Naples, Southwest Florida</p>
            <p className="text-slate-500 text-sm">Licensed & Insured — Servicing all of USA</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function SuccessMessage({ onReset }) {
  return (
    <div data-testid="contact-success" className="glass-card p-10 flex flex-col items-center justify-center text-center h-full min-h-[400px]">
      <CheckCircle size={48} className="text-emerald-400 mb-4" />
      <h3 className="text-2xl font-medium mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
        Message Sent!
      </h3>
      <p className="text-slate-400">We'll get back to you within 24 hours.</p>
      <button
        onClick={onReset}
        className="mt-6 text-blue-400 hover:text-blue-300 text-sm underline underline-offset-4"
      >
        Send another message
      </button>
    </div>
  );
}

function ContactFormFields({ form, onChange, onSubmit, status }) {
  return (
    <form onSubmit={onSubmit} data-testid="contact-form" className="glass-card p-6 md:p-8 space-y-5">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="contact-name" className="text-sm text-slate-400 mb-1.5 block">Name *</label>
          <input id="contact-name" aria-label="Your full name" type="text" name="name" autoComplete="name" required value={form.name} onChange={onChange}
            data-testid="contact-input-name" placeholder="Your full name" className="input-dark w-full" />
        </div>
        <div>
          <label htmlFor="contact-email" className="text-sm text-slate-400 mb-1.5 block">Email *</label>
          <input id="contact-email" aria-label="Email address" type="email" name="email" autoComplete="email" inputMode="email" required value={form.email} onChange={onChange}
            data-testid="contact-input-email" placeholder="you@email.com" className="input-dark w-full" />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="contact-phone" className="text-sm text-slate-400 mb-1.5 block">Phone</label>
          <input id="contact-phone" aria-label="Phone number" type="tel" name="phone" autoComplete="tel" inputMode="tel" value={form.phone} onChange={onChange}
            data-testid="contact-input-phone" placeholder="(239) 000-0000" className="input-dark w-full" />
        </div>
        <div>
          <label htmlFor="contact-service" className="text-sm text-slate-400 mb-1.5 block">Service Interest</label>
          <select id="contact-service" aria-label="Service interest" name="service" value={form.service} onChange={onChange}
            data-testid="contact-select-service" className="input-dark w-full appearance-none cursor-pointer">
            <option value="" className="bg-[#0A111C]">Select a service...</option>
            {SERVICE_OPTIONS.map((opt) => (
              <option key={opt} value={opt} className="bg-[#0A111C]">{opt}</option>
            ))}
          </select>
        </div>
      </div>

      <div>
        <label htmlFor="contact-address" className="text-sm text-slate-400 mb-1.5 block">Address</label>
        <AddressAutocomplete id="contact-address" value={form.address} onChange={onChange} className="input-dark w-full" />
      </div>

      <div>
        <label htmlFor="contact-message" className="text-sm text-slate-400 mb-1.5 block">Message *</label>
        <textarea id="contact-message" aria-label="Project message" name="message" required rows={4} value={form.message} onChange={onChange}
          data-testid="contact-input-message" placeholder="Tell us about your project..." className="input-dark w-full resize-none" />
      </div>

      {status === "error" && (
        <p data-testid="contact-error" className="text-red-400 text-sm">
          Something went wrong. Please try again or call us directly.
        </p>
      )}

      <label className="flex items-start gap-3 cursor-pointer select-none group" data-testid="newsletter-optin-label">
        <input
          type="checkbox"
          name="newsletterOptIn"
          checked={form.newsletterOptIn}
          onChange={onChange}
          data-testid="contact-newsletter-checkbox"
          className="mt-1 w-4 h-4 flex-shrink-0 accent-blue-600 cursor-pointer"
        />
        <span className="text-sm text-slate-400 leading-relaxed group-hover:text-slate-300 transition-colors">
          Send me occasional smart-home & tech tips from I.T. Help of SWFL (twice a week, unsubscribe anytime).
        </span>
      </label>

      <button type="submit" disabled={status === "sending"} data-testid="contact-submit-btn"
        className="w-full bg-blue-600 text-white hover:bg-blue-700 font-medium px-8 py-4 rounded-lg transition-all btn-glow text-base flex items-center justify-center gap-2 disabled:opacity-60">
        {status === "sending" ? "Sending..." : (<>Send Message <Send size={16} /></>)}
      </button>
    </form>
  );
}

export default function ContactForm() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", address: "", service: "", message: "", newsletterOptIn: true });
  const [status, setStatus] = useState("idle");

  const handleChange = (e) => {
    const { name, type, checked, value } = e.target;
    setForm({ ...form, [name]: type === "checkbox" ? checked : value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      const { newsletterOptIn, ...contactPayload } = form;
      await axios.post(`${API}/contact`, contactPayload);

      if (newsletterOptIn && form.email) {
        // Fire-and-forget: don't fail the lead submission if this errors
        axios
          .post(`${API}/newsletter/subscribe`, {
            email: form.email,
            name: form.name,
            source: "contact-form",
          })
          .catch(() => { /* silently ignore */ });
        trackEvent("newsletter_subscribe", { source: "contact-form" });
      }

      setStatus("sent");
      trackEvent("contact_form_submit", { service: form.service || "unspecified" });
      setForm({ name: "", email: "", phone: "", address: "", service: "", message: "", newsletterOptIn: true });
    } catch {
      setStatus("error");
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-24 md:py-32" aria-label="Contact Us">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          <ContactInfo />
          <div>
            {status === "sent"
              ? <SuccessMessage onReset={() => setStatus("idle")} />
              : <ContactFormFields form={form} onChange={handleChange} onSubmit={handleSubmit} status={status} />
            }
          </div>
        </div>
      </div>
    </section>
  );
}
